import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Download, FileSpreadsheet, FileText, Check, X } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { KolDataWithDates } from '@/types/table';
import * as XLSX from 'xlsx';

interface ExportColumn {
  id: string;
  label: string;
  key: keyof KolDataWithDates | 'readableKolName' | 'readablePlatform' | 'readableRegion' | 'readableHandler' | 'readableTier' | 'readableContentId' | 'readablePics';
  needsConversion?: boolean;
  isDefault?: boolean;
}

interface KolPostingLinksExportDialogProps {
  data: KolDataWithDates[];
  selectedRows?: string[];
  totalCount?: number;
  currentFilters?: any;
}

export function KolPostingLinksExportDialog({ 
  data, 
  selectedRows = [], 
  totalCount = 0,
  currentFilters 
}: KolPostingLinksExportDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('xlsx');
  const [exportScope, setExportScope] = useState<'all' | 'filtered' | 'selected'>('filtered');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0, isLoading: false });
  const [kolDbData, setKolDbData] = useState<any[]>([]);
  const [topicsData, setTopicsData] = useState<any[]>([]);
  const [picsData, setPicsData] = useState<any[]>([]);
  const { toast } = useToast();

  // Define available columns for export
  const availableColumns: ExportColumn[] = [
    { id: 'name', label: 'Content Creator Name', key: 'name', isDefault: true },
    { id: 'readableKolName', label: 'KOL DB Name', key: 'readableKolName', needsConversion: true },
    { id: 'channel', label: 'Channel', key: 'channel', isDefault: true },
    { id: 'readableHandler', label: 'Social Media Handler', key: 'readableHandler', needsConversion: true },
    { id: 'readablePlatform', label: 'Platform', key: 'readablePlatform', needsConversion: true },
    { id: 'readableRegion', label: 'Region', key: 'readableRegion', needsConversion: true },
    { id: 'readableTier', label: 'Tier', key: 'readableTier', needsConversion: true },
    { id: 'language', label: 'Language', key: 'language', isDefault: true },
    { id: 'type', label: 'Content Type', key: 'type', isDefault: true },
    { id: 'post_type', label: 'KOL Type', key: 'post_type', isDefault: true },
    { id: 'post_link', label: 'Post Link', key: 'post_link', isDefault: true },
    { id: 'content_views', label: 'Content Views', key: 'content_views', isDefault: true },
    { id: 'expense', label: 'Expense', key: 'expense', isDefault: true },
    { id: 'readableContentId', label: 'Content ID', key: 'readableContentId', needsConversion: true, isDefault: true },
    { id: 'business_unit', label: 'Business Unit', key: 'business_unit' },
    { id: 'notes', label: 'Notes', key: 'notes' },
    { id: 'readablePics', label: 'PICs', key: 'readablePics', needsConversion: true, isDefault: true },
    { id: 'posting_date', label: 'Posting Date', key: 'posting_date', isDefault: true },
    { id: 'integration_enable', label: 'Integration Enabled', key: 'integration_enable' },
    { id: 'created_at', label: 'Created At', key: 'created_at' },
    { id: 'updated_at', label: 'Updated At', key: 'updated_at' }
  ];

  // Initialize selected columns with defaults when dialog opens
  React.useEffect(() => {
    if (isOpen && selectedColumns.length === 0) {
      const defaultColumns = availableColumns
        .filter(col => col.isDefault)
        .map(col => col.id);
      setSelectedColumns(defaultColumns);
    }
  }, [isOpen]);

  // Fetch reference data when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      fetchReferenceData();
    }
  }, [isOpen]);

  const fetchReferenceData = async () => {
    try {
      // Fetch KOL DB data
      const { data: kolDb, error: kolDbError } = await supabase
        .from('kol_db')
        .select('id, creator_name, social_media_handler, social_media_type, region, tier');
      
      if (kolDbError) throw kolDbError;
      setKolDbData(kolDb || []);

      // Fetch topics/content IDs
      const { data: topics, error: topicsError } = await supabase
        .from('topics_business_units')
        .select('content_id, name, description');
      
      if (topicsError) throw topicsError;
      setTopicsData(topics || []);

      // Fetch PICs data
      const { data: pics, error: picsError } = await supabase
        .from('kol_pic')
        .select('id, name');
      
      if (picsError) throw picsError;
      setPicsData(pics || []);

    } catch (error) {
      console.error('Error fetching reference data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch reference data for export",
        variant: "destructive"
      });
    }
  };

  const fetchAllData = async (): Promise<KolDataWithDates[]> => {
    const batchSize = 1000;
    let offset = 0;
    let allData: any[] = [];
    
    setFetchProgress({ current: 0, total: totalCount, isLoading: true });
    
    try {
      // First, get the total count to show proper progress
      const { count: actualTotal, error: countError } = await supabase
        .from('kol_tracker')
        .select('*', { count: 'exact', head: true });
      
      if (countError) throw countError;
      
      const total = actualTotal || totalCount || 0;
      setFetchProgress({ current: 0, total, isLoading: true });
      
      console.log(`Starting to fetch all ${total} records...`);

      while (true) {
        console.log(`Fetching batch starting at offset: ${offset}`);
        
        const { data: batchData, error } = await supabase
          .from('kol_tracker')
          .select('*')
          .order('created_at', { ascending: false })
          .range(offset, offset + batchSize - 1);
        
        if (error) {
          console.error('Error fetching batch:', error);
          throw error;
        }
        
        console.log(`Fetched ${batchData?.length || 0} records in this batch`);
        
        if (!batchData || batchData.length === 0) {
          console.log('No more data to fetch');
          break;
        }
        
        allData = [...allData, ...batchData];
        offset += batchData.length;
        
        // Update progress
        setFetchProgress({ 
          current: allData.length, 
          total, 
          isLoading: true 
        });
        
        console.log(`Total records fetched so far: ${allData.length}/${total}`);
        
        // If we got less than the batch size, we've reached the end
        if (batchData.length < batchSize) {
          console.log('Reached end of data (partial batch)');
          break;
        }
        
        // Add a small delay to prevent overwhelming the database
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      console.log(`Finished fetching. Total records: ${allData.length}`);
      
      setFetchProgress({ current: allData.length, total: allData.length, isLoading: false });
      
      return allData.map(row => ({
        ...row,
        campaign_bu: Array.isArray(row.campaign_bu) ? row.campaign_bu.map(String) : (row.campaign_bu ? [String(row.campaign_bu)] : []),
        pic: Array.isArray(row.pic) ? row.pic.map(String) : (row.pic ? [String(row.pic)] : []),
        posting_date: row.posting_date ? new Date(row.posting_date) : null,
        created_at: row.created_at ? new Date(row.created_at) : null,
        updated_at: row.updated_at ? new Date(row.updated_at) : null,
        updated_date: row.updated_date ? new Date(row.updated_date) : null,
      })) as KolDataWithDates[];
      
    } catch (error) {
      setFetchProgress({ current: 0, total: 0, isLoading: false });
      throw error;
    }
  };

  const getDataToExport = async (): Promise<KolDataWithDates[]> => {
    switch (exportScope) {
      case 'selected':
        return data.filter(row => selectedRows.includes(row.id));
      case 'all':
        return await fetchAllData();
      case 'filtered':
      default:
        return data;
    }
  };

  const convertToReadableData = (rowData: KolDataWithDates) => {
    const kolInfo = kolDbData.find(kol => kol.id === rowData.kol_db_id);
    
    return {
      ...rowData,
      readableKolName: kolInfo?.creator_name || rowData.name || '',
      readableHandler: kolInfo?.social_media_handler || rowData.channel || '',
      readablePlatform: kolInfo?.social_media_type || rowData.platform || '',
      readableRegion: kolInfo?.region || '',
      readableTier: kolInfo?.tier || rowData.tier || '',
      readableContentId: Array.isArray(rowData.campaign_bu) 
        ? rowData.campaign_bu.join(', ') 
        : (rowData.campaign_bu || ''),
      readablePics: Array.isArray(rowData.pic) 
        ? rowData.pic.join(', ') 
        : (rowData.pic || ''),
      posting_date: rowData.posting_date ? rowData.posting_date.toLocaleDateString() : '',
      created_at: rowData.created_at ? rowData.created_at.toLocaleString() : '',
      updated_at: rowData.updated_at ? rowData.updated_at.toLocaleString() : '',
      integration_enable: rowData.integration_enable ? 'Yes' : 'No'
    };
  };

  const handleExport = async () => {
    if (selectedColumns.length === 0) {
      toast({
        title: "No columns selected",
        description: "Please select at least one column to export",
        variant: "destructive"
      });
      return;
    }

    setIsExporting(true);

    try {
      const dataToExport = await getDataToExport();
      
      if (dataToExport.length === 0) {
        toast({
          title: "No data to export",
          description: "No records match your export criteria",
          variant: "destructive"
        });
        return;
      }

      // Convert data to readable format
      const readableData = dataToExport.map(convertToReadableData);

      // Filter columns based on selection
      const exportData = readableData.map(row => {
        const filteredRow: any = {};
        selectedColumns.forEach(colId => {
          const column = availableColumns.find(col => col.id === colId);
          if (column) {
            filteredRow[column.label] = (row as any)[column.key];
          }
        });
        return filteredRow;
      });

      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const scope = exportScope === 'selected' ? `selected-${selectedRows.length}` : 
                   exportScope === 'all' ? 'all' : 'filtered';
      const filename = `kol-posting-links-${scope}-${timestamp}`;

      if (exportFormat === 'csv') {
        downloadCSV(exportData, filename);
      } else {
        downloadXLSX(exportData, filename);
      }

      toast({
        title: "Export successful",
        description: `Exported ${exportData.length} records as ${exportFormat.toUpperCase()}`,
      });

      setIsOpen(false);

    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  const downloadCSV = (data: any[], filename: string) => {
    if (data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          const stringValue = value?.toString() || '';
          // Escape commas and quotes in CSV
          return stringValue.includes(',') || stringValue.includes('"') 
            ? `"${stringValue.replace(/"/g, '""')}"` 
            : stringValue;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const downloadXLSX = (data: any[], filename: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'KOL Posting Links');
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const toggleColumn = (columnId: string) => {
    setSelectedColumns(prev => 
      prev.includes(columnId) 
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const selectAllColumns = () => {
    setSelectedColumns(availableColumns.map(col => col.id));
  };

  const selectDefaultColumns = () => {
    setSelectedColumns(availableColumns.filter(col => col.isDefault).map(col => col.id));
  };

  const clearAllColumns = () => {
    setSelectedColumns([]);
  };

  const exportScopeOptions = [
    { value: 'filtered', label: `Current view (${data.length} records)`, description: 'Export data currently visible with applied filters' },
    { value: 'selected', label: `Selected rows (${selectedRows.length} records)`, description: 'Export only selected rows', disabled: selectedRows.length === 0 },
    { value: 'all', label: `All data (${totalCount} records)`, description: 'Export all data without filters' }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Download className="h-4 w-4" />
          Export
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export KOL Posting Links
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Export Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Export Format</Label>
            <RadioGroup value={exportFormat} onValueChange={(value: 'csv' | 'xlsx') => setExportFormat(value)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="xlsx" id="xlsx" />
                <Label htmlFor="xlsx" className="flex items-center gap-2 cursor-pointer">
                  <FileSpreadsheet className="h-4 w-4 text-green-600" />
                  Excel (XLSX) - Recommended for complex data
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="csv" id="csv" />
                <Label htmlFor="csv" className="flex items-center gap-2 cursor-pointer">
                  <FileText className="h-4 w-4 text-blue-600" />
                  CSV - Universal format, opens in any spreadsheet app
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Export Scope Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Data to Export</Label>
            <RadioGroup value={exportScope} onValueChange={(value: 'all' | 'filtered' | 'selected') => setExportScope(value)}>
              {exportScopeOptions.map(option => (
                <div key={option.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.value} id={option.value} disabled={option.disabled} />
                  <Label htmlFor={option.value} className={`cursor-pointer ${option.disabled ? 'opacity-50' : ''}`}>
                    <div>
                      <div className="font-medium">{option.label}</div>
                      <div className="text-xs text-muted-foreground">{option.description}</div>
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Progress Indicator */}
          {fetchProgress.isLoading && (
            <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Fetching Data</Label>
                <div className="text-sm text-muted-foreground">
                  {fetchProgress.current.toLocaleString()} / {fetchProgress.total.toLocaleString()} records
                </div>
              </div>
              <Progress 
                value={fetchProgress.total > 0 ? (fetchProgress.current / fetchProgress.total) * 100 : 0} 
                className="w-full"
              />
              <div className="text-xs text-muted-foreground">
                Please wait while we fetch all records from the database...
              </div>
            </div>
          )}

          {/* Column Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Columns to Include</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectDefaultColumns}>
                  Default
                </Button>
                <Button variant="outline" size="sm" onClick={selectAllColumns}>
                  Select All
                </Button>
                <Button variant="outline" size="sm" onClick={clearAllColumns}>
                  Clear All
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-60 overflow-y-auto border rounded-lg p-4">
              {availableColumns.map(column => (
                <div key={column.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={column.id}
                    checked={selectedColumns.includes(column.id)}
                    onCheckedChange={() => toggleColumn(column.id)}
                  />
                  <Label 
                    htmlFor={column.id} 
                    className="text-sm cursor-pointer flex items-center gap-1"
                  >
                    {column.label}
                    {column.needsConversion && (
                      <Badge variant="secondary" className="text-xs">
                        Enhanced
                      </Badge>
                    )}
                    {column.isDefault && (
                      <Badge variant="outline" className="text-xs">
                        Default
                      </Badge>
                    )}
                  </Label>
                </div>
              ))}
            </div>
            
            <div className="text-xs text-muted-foreground">
              <p><strong>Enhanced columns:</strong> Convert ID references to readable names (e.g., KOL DB ID → KOL Name)</p>
              <p><strong>Selected:</strong> {selectedColumns.length} of {availableColumns.length} columns</p>
            </div>
          </div>

          {/* Export Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport}
              disabled={isExporting || selectedColumns.length === 0}
              className="gap-2"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export {exportFormat.toUpperCase()}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}